import { NavLink, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { BookOpen, ChevronDown, Flame, Gamepad2, Hand, Info, Languages, MessageSquareText, Target, Zap } from "lucide-react";
import { Pill } from "../../ui";
import { currentStreak, selectProgress } from "../../features/progress/progressSlice";
import { today } from "../../features/progress/dates";
import { selectSignLanguage, setSignLanguage } from "../../features/settings/settingsSlice";
import { COMING_SOON_LANGUAGES, SIGN_LANGUAGES, SignLanguageId } from "../../data/signLanguages";
import styles from "./NavBar.module.css";

// On phones these become the bottom tab bar, where the icon carries the link.
const LINKS = [
  { name: "Learn", to: "/", icon: BookOpen },
  { name: "Practice", to: "/practice", icon: Target },
  { name: "Translate", to: "/translate", icon: MessageSquareText },
  { name: "Games", to: "/games", icon: Gamepad2 },
  { name: "Hand signs", to: "/handSigns", icon: Hand },
  { name: "About", to: "/about", icon: Info },
];

export default function NavBar() {
  const dispatch = useDispatch();
  const progress = useSelector(selectProgress);
  const language = useSelector(selectSignLanguage);
  const streak = currentStreak(progress, today());
  return (
    <header className={styles.header}>
      <div className={styles.inner}>
        <Link to="/" className={styles.brand} aria-label="Signi home">
          <svg width="34" height="34" viewBox="0 0 34 34" aria-hidden="true">
            <rect width="34" height="34" rx="10" fill="#4F46E5" />
            <path
              d="M11 24V14.5a1.6 1.6 0 0 1 3.2 0V19M14.2 18V11.5a1.6 1.6 0 0 1 3.2 0V18M17.4 18V12.5a1.6 1.6 0 0 1 3.2 0V19M20.6 19v-4a1.6 1.6 0 0 1 3.2 0v4.5c0 3.6-2.6 6.5-6.4 6.5h-.6c-2.6 0-4.4-1.2-5.6-3.4L9 18.8a1.5 1.5 0 0 1 2.4-1.7"
              fill="none"
              stroke="#fff"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>Signi</span>
        </Link>
        <nav aria-label="Main" className={styles.nav}>
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) => (isActive ? `${styles.link} ${styles.active}` : styles.link)}
            >
              <l.icon size={22} aria-hidden="true" className={styles.linkIcon} />
              <span>{l.name}</span>
            </NavLink>
          ))}
        </nav>
        <label className={styles.language}>
          <Languages size={16} aria-hidden="true" />
          <span className={styles.srOnly}>Sign language</span>
          <span className={styles.current} aria-hidden="true">
            <span className={styles.currentShort}>{language.short} · </span>
            {language.name}
          </span>
          <ChevronDown size={16} aria-hidden="true" />
          <select
            value={language.id}
            onChange={(e) => dispatch(setSignLanguage(e.target.value as SignLanguageId))}
          >
            {Object.values(SIGN_LANGUAGES).map((l) => (
              <option key={l.id} value={l.id}>
                {l.short} · {l.name}
              </option>
            ))}
            {COMING_SOON_LANGUAGES.map((l) => (
              <option key={l.short} disabled>
                {l.short} · {l.name} (coming soon)
              </option>
            ))}
          </select>
        </label>
        <div className={styles.stats}>
          <Pill tone="amber" icon={<Flame size={16} aria-hidden="true" />}>
            {streak} days
          </Pill>
          <Pill tone="indigo" icon={<Zap size={16} aria-hidden="true" />}>
            {progress.xp} XP
          </Pill>
        </div>
      </div>
    </header>
  );
}
